import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { Button } from "@navi-soft/ui/button";
import { TextField } from "@navi-soft/ui/text-field";
import {
  AuthCard,
  authErrorClassName,
  authFieldClassName,
  authFormClassName,
  authLinkClassName,
} from "@/components/auth-card";
import { login } from "@/lib/api";

const LANDING_ORIGIN =
  import.meta.env.VITE_LANDING_ORIGIN ?? "https://navi-consortium.ru";

export const Route = createFileRoute("/login")({
  validateSearch: (search: Record<string, unknown>): { returnTo?: string } => ({
    returnTo: typeof search.returnTo === "string" ? search.returnTo : undefined,
  }),
  component: LoginPage,
});

function LoginPage() {
  const t = useTranslations("app");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const search = Route.useSearch();
  const returnTo = search.returnTo ?? "/";
  const [loginName, setLoginName] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [hint, setHint] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setHint(null);
    try {
      const res = await login(loginName, password);
      if (!res.ok) {
        setHint(loginErrorMessage(t, res.error));
        return;
      }
      await queryClient.invalidateQueries({ queryKey: ["me"] });
      const target =
        returnTo.startsWith("/") && !returnTo.startsWith("//") ? returnTo : "/";
      await navigate({ to: target });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthCard
      title={t("loginTitle")}
      footer={
        <>
          <Link to="/forgot" className={authLinkClassName()}>
            {t("forgotLink")}
          </Link>
          <a href={LANDING_ORIGIN} className={authLinkClassName()}>
            {t("backToLanding")}
          </a>
        </>
      }
    >
      <form onSubmit={(e) => void onSubmit(e)} className={authFormClassName()}>
        <TextField
          size="lg"
          type="text"
          name="login"
          label={t("loginLabel")}
          autoComplete="username"
          autoFocus
          value={loginName}
          onChange={(e) => setLoginName(e.target.value)}
          className={authFieldClassName()}
          required
        />
        <TextField
          size="lg"
          type="password"
          name="password"
          label={t("passwordLabel")}
          showPasswordLabel={t("showPassword")}
          hidePasswordLabel={t("hidePassword")}
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className={authFieldClassName()}
          required
        />
        {hint ? <p className={authErrorClassName()}>{hint}</p> : null}
        <Button
          type="submit"
          variant="primary"
          size="lg"
          className="mt-1 w-full"
          loading={submitting}
        >
          {submitting ? t("submitting") : t("submit")}
        </Button>
      </form>
    </AuthCard>
  );
}

function loginErrorMessage(
  t: (key: string) => string,
  error: string,
): string {
  switch (error) {
    case "invalid_credentials":
    case "login_password_required":
      return t("loginFailedInvalid");
    case "no_network_contract_or_unknown_login":
      return t("loginFailedNoContract");
    case "zitadel_not_configured":
    case "zitadel_unreachable":
    case "login_lookup_failed":
    case "session_resolve_failed":
      return t("loginFailedUnavailable");
    default:
      return t("loginFailed");
  }
}
